import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { Icon } from "@/components/Icon";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";
import { TaskList } from "@/components/TaskList";
import type { TaskStatus } from "@shared/types";

/**
 * Global Tasks page — designs/TaskList.html
 *
 * Renders the design's `.tl` grid via the shared <TaskList /> component.
 * This route owns: page header, toolbar (Group / Assignee:Me / search),
 * data fetching, and the bulk-action bar. The grid markup itself lives
 * in <TaskList />, which is also used by /projects/<id>/list.
 */

function TasksPageComponent() {
  const [groupByStatus, setGroupByStatus] = useState(true);
  const [filterMe, setFilterMe] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Bumped after each bulk action so TaskList drops its checkbox state too.
  const [selectionResetKey, setSelectionResetKey] = useState(0);

  const qc = useQueryClient();

  const meQ = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const tasksQ = useQuery({
    queryKey: queryKeys.tasksAll(),
    queryFn: api.tasksAll,
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });

  const userNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, m.name ?? m.email ?? m.id.slice(0, 8));
    }
    return map;
  }, [teamQ.data]);

  const userImages = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, m.image ?? null);
    }
    return map;
  }, [teamQ.data]);

  const projectsMap = useMemo(() => {
    const map = new Map<string, { name: string; color: string | null }>();
    for (const p of projectsQ.data?.projects ?? []) {
      map.set(p.id, { name: p.name, color: p.color ?? null });
    }
    return map;
  }, [projectsQ.data]);

  const meId = meQ.data?.user.id;

  const filtered = useMemo(() => {
    const raw = tasksQ.data?.items ?? [];
    const q = search.trim().toLowerCase();
    return raw.filter((t) => {
      if (filterMe && meId && !t.assigneeIds?.includes(meId)) return false;
      if (q) {
        const inTitle = t.title.toLowerCase().includes(q);
        const proj = projectsMap.get(t.projectId)?.name?.toLowerCase() ?? "";
        const inProj = proj.includes(q);
        if (!inTitle && !inProj) return false;
      }
      return true;
    });
  }, [tasksQ.data, filterMe, meId, search, projectsMap]);

  // One mutation for every bulk action — Mark done, Assign to me, Move,
  // Priority are all "PATCH the same body onto each selected task".
  const bulkPatch = useMutation({
    mutationFn: async (body: {
      status?: TaskStatus;
      priority?: number;
      assigneeId?: string;
    }) => {
      await Promise.all(selectedIds.map((id) => api.patchTask(id, body)));
    },
    onSuccess: () => {
      // Invalidate the full task surface set for each affected task so the
      // inbox badge, my-tasks queue, dashboard, project board, and per-task
      // detail view all refresh — not just this page's `tasksAll` cache.
      const cached = tasksQ.data?.items ?? [];
      const byId = new Map(cached.map((t) => [t.id, t]));
      for (const taskId of selectedIds) {
        const projectId = byId.get(taskId)?.projectId;
        invalidateForTask(qc, { projectId, taskId });
      }
      setSelectedIds([]);
      setSelectionResetKey((k) => k + 1);
    },
  });

  const isLoading = tasksQ.isLoading;
  const isError = tasksQ.isError;

  return (
    <div className="pg-wrap">
      {/* Page header */}
      <header className="pg-head">
        <h1>My Tasks</h1>
        {!isLoading && !isError && (
          <span className="count">{filtered.length}</span>
        )}
        <div className="right">
          <SegmentedToggle
            options={[
              { value: "board", label: "Board", icon: "board" },
              { value: "list", label: "List", icon: "clip" },
            ]}
            value="list"
            onChange={(v) => {
              if (v === "board") window.location.href = "/";
            }}
          />
          {/* "Add task" needs a project in scope. We use the user's first
              project as the default destination; if they have none we fall
              back to the Projects list so they can create one first. */}
          {projectsQ.data?.projects[0] ? (
            <Link
              to="/projects/$projectId/tasks/new"
              params={{ projectId: projectsQ.data.projects[0].id }}
              className="btn btn-primary btn-sm"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Icon name="plus" size={14} />
              Add task
            </Link>
          ) : (
            <Link
              to="/projects/new"
              className="btn btn-primary btn-sm"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Icon name="plus" size={14} />
              Add task
            </Link>
          )}
        </div>
      </header>

      {/* Toolbar */}
      <div className="toolbar">
        <button
          type="button"
          className="chip-btn"
          onClick={() => setGroupByStatus((g) => !g)}
        >
          <Icon name="grid" size={14} />
          Group: {groupByStatus ? "Status" : "None"}
        </button>
        <button
          type="button"
          className={`chip-btn${filterMe ? " on" : ""}`}
          onClick={() => setFilterMe((f) => !f)}
        >
          <Icon name="filter" size={14} />
          Filter
        </button>
        <button
          type="button"
          className={`chip-btn${filterMe ? " on" : ""}`}
          onClick={() => setFilterMe((f) => !f)}
        >
          <Icon name="users" size={14} />
          Assignee: {filterMe ? "Me" : "All"}
        </button>
        <div className="spacer" />
        <div className="input-search" style={{ width: 240 }}>
          <Icon name="search" size={14} />
          <input
            type="search"
            className="input"
            placeholder="Search tasks…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <Skeleton count={8} height={44} />
      ) : isError ? (
        <EmptyState
          icon="close"
          title="Couldn't load your tasks."
          caption="Refresh the page or try again in a moment."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="check"
          title="Nothing on your plate."
          caption="Pick up something from the team backlog."
          actionLabel="Browse projects"
          onAction={() => {
            window.location.href = "/projects";
          }}
        />
      ) : (
        <TaskList
          tasks={filtered}
          userNames={userNames}
          userImages={userImages}
          projects={projectsMap}
          showProjectColumn
          groupByStatus={groupByStatus}
          onSelectionChange={setSelectedIds}
          selectionResetKey={selectionResetKey}
        />
      )}

      {/* Bulk action bar */}
      <div className={`bulkbar${selectedIds.length > 0 ? " show" : ""}`}>
        <span className="bsel">
          <b>{selectedIds.length}</b> selected
        </span>
        <div className="bdiv" />
        <button
          type="button"
          onClick={() => bulkPatch.mutate({ status: "done" })}
          disabled={bulkPatch.isPending}
        >
          <Icon name="check" size={14} />
          Mark done
        </button>
        {/* Cross-project bulk assign to an arbitrary member is ill-defined
            (the assignee must be a member of every task's project); "me" is
            the one target guaranteed valid for every task I can see. */}
        <button
          type="button"
          onClick={() => meId && bulkPatch.mutate({ assigneeId: meId })}
          disabled={bulkPatch.isPending || !meId}
        >
          <Icon name="users" size={14} />
          Assign to me
        </button>
        <label className="bsellbl">
          <Icon name="arrow" size={14} />
          <select
            className="bselect"
            aria-label="Move selected tasks to status"
            value=""
            disabled={bulkPatch.isPending}
            onChange={(e) => {
              const v = e.target.value as TaskStatus | "";
              if (v) bulkPatch.mutate({ status: v });
            }}
          >
            <option value="" disabled>
              Move to…
            </option>
            <option value="backlog">Backlog</option>
            <option value="todo">To Do</option>
            <option value="doing">Doing</option>
            <option value="done">Done</option>
          </select>
        </label>
        <label className="bsellbl">
          <Icon name="flag" size={14} />
          <select
            className="bselect"
            aria-label="Set priority for selected tasks"
            value=""
            disabled={bulkPatch.isPending}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= 1 && v <= 4) bulkPatch.mutate({ priority: v });
            }}
          >
            <option value="" disabled>
              Priority…
            </option>
            <option value="1">Urgent</option>
            <option value="2">High</option>
            <option value="3">Medium</option>
            <option value="4">Low</option>
          </select>
        </label>
        <div className="bdiv" />
        <button
          type="button"
          className="bclose"
          onClick={() => {
            setSelectedIds([]);
            setSelectionResetKey((k) => k + 1);
          }}
          aria-label="Clear selection"
        >
          <Icon name="close" size={14} />
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/tasks/")({
  component: TasksPageComponent,
});
